import { action } from "typesafe-actions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { api } from "../../api/api";
import {
  IUpsellLead,
  IUpsertUpsellLeadPayload,
  IUpsellInsightData,
  IFetchUpsellInsightPayload,
  IFetchRecommendedTemplatePayload,
  IAutoGenerateCampaignPayload,
  IAutoGenerateCampaignResponse,
} from "./upsellLeads.types";
import { endPoints } from "../../constants/endPoints";
import { IWhatsAppTemplate } from "../whatsappTemplate/whatsappTemplate.types";

// Action Types
export const FETCH_UPSELL_LEADS_LIST_PROGRESS =
  "FETCH_UPSELL_LEADS_LIST_PROGRESS";
export const FETCH_UPSELL_LEADS_LIST_SUCCESS =
  "FETCH_UPSELL_LEADS_LIST_SUCCESS";
export const FETCH_UPSELL_LEADS_LIST_FAILED = "FETCH_UPSELL_LEADS_LIST_FAILED";

export const FETCH_UPSELL_INSIGHT_PROGRESS = "FETCH_UPSELL_INSIGHT_PROGRESS";
export const FETCH_UPSELL_INSIGHT_SUCCESS = "FETCH_UPSELL_INSIGHT_SUCCESS";
export const FETCH_UPSELL_INSIGHT_FAILED = "FETCH_UPSELL_INSIGHT_FAILED";

export const FETCH_RECOMMENDED_TEMPLATE_PROGRESS =
  "FETCH_RECOMMENDED_TEMPLATE_PROGRESS";
export const FETCH_RECOMMENDED_TEMPLATE_SUCCESS =
  "FETCH_RECOMMENDED_TEMPLATE_SUCCESS";
export const FETCH_RECOMMENDED_TEMPLATE_FAILED =
  "FETCH_RECOMMENDED_TEMPLATE_FAILED";

export const AUTO_GENERATE_CAMPAIGN_PROGRESS =
  "AUTO_GENERATE_CAMPAIGN_PROGRESS";
export const AUTO_GENERATE_CAMPAIGN_SUCCESS = "AUTO_GENERATE_CAMPAIGN_SUCCESS";
export const AUTO_GENERATE_CAMPAIGN_FAILED = "AUTO_GENERATE_CAMPAIGN_FAILED";

export const CLEAR_UPSELL_LEADS_STATE = "CLEAR_UPSELL_LEADS_STATE";
export const CLEAR_UPSELL_INSIGHT_STATE = "CLEAR_UPSELL_INSIGHT_STATE";
export const CLEAR_RECOMMENDED_TEMPLATE_STATE =
  "CLEAR_RECOMMENDED_TEMPLATE_STATE";

// Action Creators
export const fetchUpsellLeadsListProgress = () =>
  action(FETCH_UPSELL_LEADS_LIST_PROGRESS);

export const fetchUpsellLeadsListSuccess = (
  data: IUpsellLead[],
  totalRecords: number
) => action(FETCH_UPSELL_LEADS_LIST_SUCCESS, { data, totalRecords });

export const fetchUpsellLeadsListFailed = () =>
  action(FETCH_UPSELL_LEADS_LIST_FAILED);

export const clearUpsellLeadsState = () => action(CLEAR_UPSELL_LEADS_STATE);

/**
 * Fetch Upsell Leads List
 * API: /dashboard/get-upsell-leads?pageNo=1&itemPerPage=10&pageLimit=11
 */

interface IFetchUpsellLeadsListAsyncParams {
  pageNumber: number;
  itemPerPage: number;
  pageLimit: number;
  company_outlet_uuid?: string;
  status?: string;
  date?: IDataTableV2DateState["dates"];
  searchValue?: IDatatableV2AdvancedSearchFilter;
  searchByName?: string;
  upsellLeadsUuid?: string;
}
export const fetchUpsellLeadsListAsync =
  (
    params: IFetchUpsellLeadsListAsyncParams
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    const {
      pageNumber,
      itemPerPage,
      pageLimit,
      company_outlet_uuid,
      status,
      date,
      searchValue,
      searchByName,
      upsellLeadsUuid,
    } = params;
    dispatch(fetchUpsellLeadsListProgress());
    try {
      dispatch(saveLoaderProgress());
      let searchQuery = "";

      // Add search by name if provided
      if (searchByName) {
        searchQuery += `&name_of_guest=${searchByName}`;
      }
      if (company_outlet_uuid) {
        searchQuery += `&company_outlet_uuid=${company_outlet_uuid}`;
      }

      // Add specific upsell lead uuid filter
      if (upsellLeadsUuid) {
        searchQuery += `&upsell_leads_uuid=${upsellLeadsUuid}`;
      }

      // Add advanced filter if provided
      if (searchValue?.length ?? 0) {
        const advanceFilter = [...(searchValue || [])];
        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      // Add date range if provided
      if (date?.fromDate && date?.toDate) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      // Add status filter if provided
      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const response = await api.get(
        `/dashboard/get-upsell-leads?pageNo=${pageNumber}&itemPerPage=${itemPerPage}&pageLimit=${pageLimit}` +
          searchQuery
      );

      const data: IUpsellLead[] = response.data.data || [];
      const totalRecords = response.data.totalRecords || 0;

      dispatch(fetchUpsellLeadsListSuccess(data, totalRecords));
    } catch (error: any) {
      dispatch(fetchUpsellLeadsListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message || "Failed to fetch upsell leads",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

/**
 * Upsert Upsell Leads
 * API: /dashboard/upsert-upsell-leads
 * Payload: Array of IUpsertUpsellLeadPayload
 */
export const upsertUpsellLeadsAsync =
  (
    data: IUpsertUpsellLeadPayload[],
    onCallback?: (isSuccess: boolean, responseData?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      const response = await api.post("/dashboard/upsert-upsell-leads", data);

      dispatch(saveLoaderCompleted());

      const isUpdate = data.some((item) => item.upsell_leads_uuid !== null);

      dispatch(
        showMessage({
          type: "success",
          message: isUpdate
            ? "Upsell lead(s) updated successfully"
            : "Upsell lead(s) created successfully",
          displayAs: "snackbar",
        })
      );

      onCallback?.(true, response.data);
      return response.data;
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message || "Failed to save upsell lead(s)",
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

/**
 * Upsert Single Upsell Lead (convenience method)
 */
export const upsertSingleUpsellLeadAsync =
  (
    data: IUpsertUpsellLeadPayload,
    onCallback?: (isSuccess: boolean, responseData?: any) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    // @ts-ignore
    dispatch(upsertUpsellLeadsAsync([data], onCallback));
  };

// ============== UPSELL INSIGHT ACTIONS ==============

// Action Creators for Upsell Insight
export const fetchUpsellInsightProgress = () =>
  action(FETCH_UPSELL_INSIGHT_PROGRESS);

export const fetchUpsellInsightSuccess = (data: IUpsellInsightData[]) =>
  action(FETCH_UPSELL_INSIGHT_SUCCESS, { data });

export const fetchUpsellInsightFailed = (error?: string) =>
  action(FETCH_UPSELL_INSIGHT_FAILED, { error });

export const clearUpsellInsightState = () => action(CLEAR_UPSELL_INSIGHT_STATE);

export const fetchUpsellInsightAsync =
  (
    payload: IFetchUpsellInsightPayload,
    onCallback?: (isSuccess: boolean, data?: IUpsellInsightData[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchUpsellInsightProgress());
    try {
      dispatch(saveLoaderProgress());
      const response = await api.post(
        `${endPoints.upsell.upsellInsight}`,
        payload
      );

      const data: IUpsellInsightData[] = response.data.data || [];
      dispatch(fetchUpsellInsightSuccess(data));
      onCallback?.(true, data);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to fetch upsell insights";
      dispatch(fetchUpsellInsightFailed(errorMessage));
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// ============== RECOMMENDED TEMPLATE ACTIONS ==============

// Action Creators for Recommended Template
export const fetchRecommendedTemplateProgress = () =>
  action(FETCH_RECOMMENDED_TEMPLATE_PROGRESS);

export const fetchRecommendedTemplateSuccess = (data: IWhatsAppTemplate[]) =>
  action(FETCH_RECOMMENDED_TEMPLATE_SUCCESS, { data });

export const fetchRecommendedTemplateFailed = (error?: string) =>
  action(FETCH_RECOMMENDED_TEMPLATE_FAILED, { error });

export const clearRecommendedTemplateState = () =>
  action(CLEAR_RECOMMENDED_TEMPLATE_STATE);

export const fetchRecommendedTemplateAsync =
  (
    payload: IFetchRecommendedTemplatePayload,
    onCallback?: (isSuccess: boolean, data?: IWhatsAppTemplate[]) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchRecommendedTemplateProgress());
    try {
      const response = await api.get(`${endPoints.upsell.upsellTemplate}`, {
        params: {
          company_outlet_uuid: payload.company_outlet_uuid,
          // venue_templates: payload.venue_templates,
          // service_templates: payload.service_templates,
          leads_uuid: payload.leads_uuid,
          others: payload.others,
        },
      });

      const data: IWhatsAppTemplate[] = response.data.data || [];
      dispatch(fetchRecommendedTemplateSuccess(data));
      onCallback?.(true, data);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to fetch recommended templates";
      dispatch(fetchRecommendedTemplateFailed(errorMessage));
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

// ============== AUTO GENERATE CAMPAIGN ACTIONS ==============

// Action Creators for Auto Generate Campaign
export const autoGenerateCampaignProgress = () =>
  action(AUTO_GENERATE_CAMPAIGN_PROGRESS);

export const autoGenerateCampaignSuccess = (
  data: IAutoGenerateCampaignResponse
) => action(AUTO_GENERATE_CAMPAIGN_SUCCESS, { data });

export const autoGenerateCampaignFailed = (error?: string) =>
  action(AUTO_GENERATE_CAMPAIGN_FAILED, { error });

/**
 * Auto Generate Campaign
 * API: /campaign/auto-generate-campaign
 */
export const autoGenerateCampaignAsync =
  (
    payload: IAutoGenerateCampaignPayload,
    onCallback?: (
      isSuccess: boolean,
      data?: IAutoGenerateCampaignResponse
    ) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(autoGenerateCampaignProgress());
    try {
      const response = await api.post(
        `${endPoints.upsell.autoGenerateCampaign}`,
        payload
      );

      const data: IAutoGenerateCampaignResponse = response.data.data;

      if (data?.market_campaign_uuid) {
        dispatch(autoGenerateCampaignSuccess(data));
        dispatch(
          showMessage({
            type: "success",
            message: "Campaign generated successfully!",
            displayAs: "snackbar",
          })
        );
        onCallback?.(true, data);
      } else {
        dispatch(
          autoGenerateCampaignFailed("Campaign generated but no UUID returned")
        );
        dispatch(
          showMessage({
            type: "warning",
            message: "Campaign generated but no UUID returned",
            displayAs: "snackbar",
          })
        );
        onCallback?.(false);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Failed to generate campaign";
      dispatch(autoGenerateCampaignFailed(errorMessage));
      dispatch(
        showMessage({
          type: "error",
          message: errorMessage,
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };
